import { useQuery } from '@tanstack/react-query'
import {
  useAccountFeatures,
  useAccountSummary,
  useCreditAccounts
} from 'const/syfApiQueries'
import { useNavigate } from 'react-router-dom'
import { analyticsConfig } from 'syf-js-utilities/helpers/triggerAnalyticsEvent'
import config from 'const/config'
import featureFlags from 'const/featureFlags'
import handleAccountLink from 'helpers/handleAccountLink'
import routes from 'const/routes'
import findAccount from 'helpers/findAccount'
import { debounce } from 'syf-js-utilities'
import useWindowSize from 'syf-component-library/ui/customHooks/useWindowWidth'
import handleRewardsPlatform from 'helpers/handleRewardsPlatform'
import queryClient from 'const/queryClient'
import clearSessionStorage from 'helpers/clearSessionStorage'
import handleRevokeAccessToken from 'helpers/handleRevokeAccessToken'
import redirectToUrl from 'helpers/redirectToUrl'
import handoverToPayAsGuest from 'helpers/handoverToPayAsGuest'
import handoverToRegisterAccount from 'helpers/handoverToRegisterAccount'
import getRewardsResponse from 'helpers/getRewardsResponse'
import { creditBalanceRefundModalContainerId } from 'const/modalContainerId'
import { modalStatusAtom, selectedAccountAtom } from 'store/atoms'
import { useSetAtom } from 'jotai'
import { MAPFlowTypes } from 'make-a-payment/lib/typings/deepLink'

interface RewardsResponse {
  samlToken: string
}

const useGetCTAHandlers = (accountId: string) => {
  const navigate = useNavigate()
  const windowWidth = useWindowSize()

  const updateModalStatus = useSetAtom(modalStatusAtom)
  const setSelectedAccount = useSetAtom(selectedAccountAtom)
  const { data: { accounts = [] } = {} } = useCreditAccounts()
  const { data: accountSummary } = useAccountSummary(accountId)
  const { data: accountFeatures } = useAccountFeatures(accountId)
  const account = findAccount(accounts, accountId)

  const { product } = accountSummary || {}
  const { type: productType, clientId = '', lineOfBusiness } = product || {}

  const { ssoEnabled, rewardsPlatformDestination, accessToRewardsMfe } =
    accountFeatures?.rewardDetails || {}

  const statementsButtonId = `statements-${accountId}`
  const rewardsButtonId = `rewards-${accountId}`
  const activityButtonId = `activity-${accountId}`
  const detailsButtonId = `details-${accountId}`
  const paymentButtonId = `map-${accountId}`
  const collectionsButtonId = `collections-${accountId}`

  const samlTokenCacheKey = ['saml-token', accountId]
  useQuery<RewardsResponse>(
    samlTokenCacheKey,
    () => getRewardsResponse(accountId),
    {
      enabled:
        Boolean(ssoEnabled) &&
        Boolean(accountId) &&
        Boolean(rewardsPlatformDestination)
    }
  )

  const handleAccountServicesClick = () => {
    setSelectedAccount(accountId)
    navigate(routes.ACCOUNT_SERVICES)
  }

  const debouncedStatementsClick = debounce(() => {
    handleAccountLink(account, clientId, windowWidth, 'statements')
  }, 250)

  const handleStatementsClick = () => {
    if (featureFlags.isStatementsMfeEnabled) {
      analyticsConfig.defaults.Product = productType
      analyticsConfig.defaults.ClientName = clientId
      setSelectedAccount(accountId)
      updateModalStatus('Statement', statementsButtonId)
    } else {
      debouncedStatementsClick()
    }
  }

  const handleRewardsClick = () => {
    const samlToken =
      queryClient.getQueryState<RewardsResponse>(samlTokenCacheKey)?.data
        ?.samlToken

    const isRewardsError = Boolean(
      queryClient.getQueryState<RewardsResponse>(samlTokenCacheKey)?.error
    )

    if (accessToRewardsMfe) {
      setSelectedAccount(accountId)
      updateModalStatus('Rewards', rewardsButtonId)
      return
    }

    if (isRewardsError) {
      navigate(routes.REWARDS_ERROR)
      return
    }

    if (rewardsPlatformDestination && ssoEnabled) {
      handleRewardsPlatform(samlToken, rewardsPlatformDestination)
      queryClient.invalidateQueries(samlTokenCacheKey)
    } else if (rewardsPlatformDestination && !ssoEnabled) {
      window.open(rewardsPlatformDestination, '_blank', 'noopener,noreferrer')
    } else {
      handleAccountLink(account, clientId, windowWidth, 'rewards')
    }
  }

  const handleActivityClick = () => {
    analyticsConfig.defaults.Product = productType
    analyticsConfig.defaults.ClientName = clientId
    updateModalStatus('Activity', activityButtonId)
    setSelectedAccount(accountId)
  }

  const handleDetailsClick = () => {
    analyticsConfig.defaults.Product = productType
    analyticsConfig.defaults.ClientName = clientId
    if (featureFlags.isAccountDetailsRouteEnabled) {
      navigate(routes.ACCOUNT_DETAILS)
    } else {
      updateModalStatus('Details', detailsButtonId)
    }
    setSelectedAccount(accountId)
  }

  const handleCollectionClick = async () => {
    if (featureFlags.isDigitalCollectionsExperienceEnabled) {
      updateModalStatus('DigitalCollections', collectionsButtonId)
      setSelectedAccount(accountId)
    } else {
      const languageId = localStorage.getItem('language')
      await handleRevokeAccessToken()
      clearSessionStorage()
      redirectToUrl(
        `${config.DCH_HOST_NAME}/?account=${accountId}&lang=${languageId}`
      )
    }
  }

  const handleCBRClick = () => {
    analyticsConfig.defaults.Product = product?.type
    analyticsConfig.defaults.ClientName = product?.clientId
    setSelectedAccount(accountId)
    if (featureFlags.isCreditBalanceRefundDrawerEnabled) {
      const manageCreditBalanceRefundButtonId =
        creditBalanceRefundModalContainerId(accountId)
      updateModalStatus(
        'CreditBalanceRefund',
        manageCreditBalanceRefundButtonId
      )
    } else {
      navigate(routes.CREDIT_BALANCE_REFUND)
    }
  }

  const handleMAPClick = () => {
    analyticsConfig.defaults.Product = productType
    analyticsConfig.defaults.ClientName = clientId
    analyticsConfig.defaults.PageKind = 'payments'

    setSelectedAccount(accountId)
    updateModalStatus('MAP', paymentButtonId)
  }

  const handleAutopayClick = () => {
    analyticsConfig.defaults.Product = productType
    analyticsConfig.defaults.ClientName = clientId
    analyticsConfig.defaults.PageKind = 'payments'

    if (account.registration.registered) {
      setSelectedAccount(accountId)
      updateModalStatus('MAP', paymentButtonId, {
        MAP: { flow: { type: MAPFlowTypes.AUTOPAY_FLOW } }
      })
    } else {
      handoverToPayAsGuest(account.product, clientId)
    }
  }

  const handleRegisterAccountClick = () => {
    handoverToRegisterAccount(lineOfBusiness, clientId)
  }

  const handleAccountClick = () => {
    handleAccountLink(account, accountSummary?.product?.clientId, windowWidth)
  }

  const handlePromotionsClick = () => {
    setSelectedAccount(accountId)
    navigate(routes.PROMOTIONS)
  }

  return {
    handleActivityClick,
    handleAutopayClick,
    handleCBRClick,
    handleCollectionClick,
    handleDetailsClick,
    handleMAPClick,
    handleRewardsClick,
    handleAccountServicesClick,
    handleStatementsClick,
    handleRegisterAccountClick,
    handleAccountClick,
    handlePromotionsClick
  }
}

export default useGetCTAHandlers
